import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SET_LABELS = ['C1', 'C2', 'C3', 'C4', 'C5'];
const SET_COLORS = [
  { active: 'bg-blue-600 text-white', ring: 'ring-blue-400' },
  { active: 'bg-purple-600 text-white', ring: 'ring-purple-400' },
  { active: 'bg-teal-600 text-white', ring: 'ring-teal-400' },
];

export function MyPredictionBlock({ storageKey, label, description, children, value, onSave, onClear, activeSet = 1, onSetChange, appliedSets }) {
  const [applied, setApplied] = useState(!!value);

  // Sync applied state when value changes (e.g. parent loaded from DB)
  useEffect(() => {
    setApplied(!!value);
  }, [value]);

  const handleSetChange = (setNum) => {
    if (onSetChange) onSetChange(setNum);
  };

  const handleSave = () => {
    if (onSave) onSave();
    setApplied(true);
    toast.success(`${label} C${activeSet} constraint applied`);
  };

  const handleClear = () => {
    if (onClear) onClear();
    setApplied(false);
    toast.info(`${label} C${activeSet} constraint cleared`);
  };

  return (
    <div className={`rounded-lg p-5 text-white transition-all ${applied ? 'bg-gradient-to-r from-emerald-600 to-green-700 ring-2 ring-emerald-400' : 'bg-gradient-to-r from-gray-600 to-gray-700'}`} data-testid={`my-prediction-${storageKey}`}>
      {/* C1/C2/C3/C4/C5 set selector */}
      <div className="flex items-center gap-1.5 mb-3" data-testid="constraint-set-tabs">
        {SET_LABELS.map((sl, i) => {
          const setNum = i + 1;
          const isActive = activeSet === setNum;
          const hasData = appliedSets ? appliedSets.includes(setNum) : false;
          return (
            <button
              key={setNum}
              onClick={() => handleSetChange(setNum)}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all ${
                isActive
                  ? `${SET_COLORS[i % SET_COLORS.length].active} ring-2 ${SET_COLORS[i % SET_COLORS.length].ring} shadow-lg`
                  : hasData
                    ? 'bg-white/20 text-white/90 ring-1 ring-white/30'
                    : 'bg-white/10 text-white/50 hover:bg-white/15'
              }`}
              data-testid={`set-${setNum}-btn`}
            >
              {sl}
            </button>
          );
        })}
        <span className="text-[10px] text-white/40 ml-2">5 independent constraint sets</span>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-bold text-lg">My {label} Prediction <span className="text-sm opacity-70">({SET_LABELS[activeSet - 1]})</span></h3>
          <p className="text-xs text-white/70">{description}</p>
        </div>
        {applied ? (
          <span className="text-xs bg-emerald-400/30 px-3 py-1.5 rounded-full font-bold flex items-center gap-1.5 ring-1 ring-emerald-300">
            <span className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse"></span> APPLIED
          </span>
        ) : (
          <span className="text-xs bg-white/10 px-3 py-1.5 rounded-full font-bold text-white/60">NOT SET</span>
        )}
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        {children}
        <Button onClick={handleSave} className={`font-bold transition-all ${applied ? 'bg-emerald-500 text-white hover:bg-emerald-400 ring-2 ring-emerald-300' : 'bg-amber-400 text-amber-900 hover:bg-amber-300'}`} data-testid={`save-${storageKey}-btn`}>
          {applied ? 'Applied' : 'Save Constraint'}
        </Button>
        {applied && (
          <Button onClick={handleClear} variant="ghost" className="text-white/70 hover:text-white text-xs" data-testid={`clear-${storageKey}-btn`}>
            Clear
          </Button>
        )}
      </div>
      {value && applied && (
        <div className="mt-3 bg-white/10 rounded px-3 py-2 inline-flex items-center gap-2">
          <span className="text-[10px] uppercase opacity-70">Current:</span>
          <span className="font-mono font-bold">{value}</span>
        </div>
      )}
    </div>
  );
}

// Read all constraints for a specific set (1, 2, or 3)
export function getConstraintsForSet(setNum) {
  const s = `_c${setNum}`;
  const posLabels = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5', lucky_star_1: 'LS1', lucky_star_2: 'LS2' };

  return {
    hmlPattern: {
      value: localStorage.getItem(`em_hml_pattern${s}`),
      applied: !!localStorage.getItem(`em_hml_pattern${s}`),
      label: 'HML Pattern (W7)',
      display: localStorage.getItem(`em_hml_pattern${s}`),
      path: '/window7',
      dep: 'independent',
    },
    signPattern: {
      value: localStorage.getItem(`em_sign_pattern${s}`),
      applied: !!localStorage.getItem(`em_sign_pattern${s}`),
      label: 'Diff Sign (W3)',
      display: localStorage.getItem(`em_sign_pattern${s}`),
      path: '/window3',
      dep: 'dependent',
      syncWith: 'W5',
    },
    comboPattern: {
      value: localStorage.getItem(`em_combo_pattern${s}`),
      applied: !!localStorage.getItem(`em_combo_pattern${s}`),
      label: '7-Combo (W5)',
      display: localStorage.getItem(`em_combo_pattern${s}`),
      path: '/window3-2',
      dep: 'dependent',
      syncWith: 'W3',
    },
    sumRange: {
      min: localStorage.getItem(`em_sum_range_min${s}`),
      max: localStorage.getItem(`em_sum_range_max${s}`),
      applied: !!localStorage.getItem(`em_sum_range_min${s}`),
      label: 'Sum Range (W14)',
      display: localStorage.getItem(`em_sum_range_min${s}`) ? `${localStorage.getItem(`em_sum_range_min${s}`)} - ${localStorage.getItem(`em_sum_range_max${s}`)}` : null,
      path: '/window14',
      dep: 'dependent',
      syncWith: 'W24',
    },
    rangePattern: {
      value: localStorage.getItem(`em_range_pattern${s}`),
      applied: !!localStorage.getItem(`em_range_pattern${s}`),
      label: 'Range Pattern (W24)',
      display: localStorage.getItem(`em_range_pattern${s}`),
      path: '/window24',
      dep: 'dependent',
      syncWith: 'W14',
    },
    sharedPrediction: {
      value: localStorage.getItem(`em_shared_prediction${s}`),
      applied: !!localStorage.getItem(`em_shared_prediction${s}`),
      label: 'Shared Numbers (W17)',
      display: (() => {
        const v = localStorage.getItem(`em_shared_prediction${s}`);
        if (!v) return null;
        const parts = v.split(',').map(x => x.trim());
        return parts.length > 1 ? parts.join(' or ') + ' shared' : v + ' shared';
      })(),
      path: '/window17',
      dep: 'independent',
    },
    tempPreference: (() => {
      const raw = localStorage.getItem(`em_w11_temp_prefs${s}`);
      const parsed = raw ? JSON.parse(raw) : {};
      const count = Object.keys(parsed).length;
      return {
        value: raw,
        applied: count > 0,
        label: 'Temp Pref (W11×W21)',
        display: count > 0 ? Object.entries(parsed).map(([k, v]) => `${posLabels[k] || k}=${v}`).join(', ') : null,
        path: '/cross-analysis',
        dep: 'independent',
      };
    })(),
  };
}

// Get all constraints — returns object with c1, c2, c3
export function getAllConstraints(setNum) {
  if (setNum) return getConstraintsForSet(setNum);
  return getConstraintsForSet(1); // default to C1
}

// Get all 3 sets
export function getAllConstraintSets() {
  return {
    c1: getConstraintsForSet(1),
    c2: getConstraintsForSet(2),
    c3: getConstraintsForSet(3),
  };
}


/**
 * Sync a specific constraint field to MongoDB via PATCH.
 * fieldsToSync: { sign_pattern: "+++++" } or { sign_pattern: null } to clear.
 */
export async function syncConstraintsToDB(setNum, fieldsToSync) {
  const setKey = `C${setNum}`;
  try {
    await axios.patch(`${API}/analysis/constraint-sets`, { set_key: setKey, fields: fieldsToSync });
  } catch {
    // Silent fail
  }
}
